import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { catchError, finalize, retry, switchMap, take, timeout } from 'rxjs/operators';
import { Observable, Subject, throwError, TimeoutError, } from 'rxjs';
import { AuthService, EXCLUDE_AUTH_URL } from './auth.service';
import { isNullOrUndefined, UtilsService } from './utils.service';
import { FAIL, isSuccess, Response } from './response';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProgressSpinnerComponent } from '../common/progress/progress-spinner.component';


@Injectable({
  providedIn: 'root'
})
export class HttpClientService implements HttpInterceptor, ErrorHandler{
    private static readonly DEFAULT_TIMEOUT: number = 7000;
    private static DOMAIN =  environment.domain;
    private static ROOT_URL = environment.rootUrl;

    dialogRef: MatDialogRef<ProgressSpinnerComponent> | undefined;

    /**
     * 스피너를 띄우기 전 기다리는 시간(ms).
     *
     * 사내망 호출은 보통 수십 ms 안에 끝나는데, 그때마다 모달 스피너를 열었다
     * 닫으면 화면이 번쩍인다 (대시보드 60초 폴링·메뉴 이동 시 특히). 이 시간
     * 안에 응답이 오면 스피너는 아예 열리지 않는다.
     */
    // 300ms 로는 사내망 왕복이 경계에 걸려 스피너가 떴다 바로 닫히는(번쩍이는)
    // 화면이 있었다. 대부분의 조회가 그 안에 끝나므로 500ms 로 둔다.
    private static readonly LOADING_DELAY: number = 500;

    /** 진행 중인 "스피너 대상" 요청 수. 0 이 될 때만 닫는다. */
    private loadingCount: number = 0;
    private loadingTimer: any = null;
    httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json;charset=UTF-8',
      })
    };

    // Singleton refresh — 동시 다발 401 호출 시 refresh 한 번만 실행, 나머지는 대기.
    //   reuseRefreshTokens(false) 정책 하에서 동일 refresh_token 으로 동시 호출되면
    //   InvalidTokenException 발생 → "로그인 필요" 알림 race 방지.
    //   Subject (BehaviorSubject 아님) — 초기값 즉시 emit 회피. 대기자는 다음 next() 한 번만 받음.
    //   next(token) = 성공, next(null) = refresh 실패 (대기자 throw 처리).
    private isRefreshing: boolean = false;
    private refreshTokenSubject: Subject<string | null> = new Subject<string | null>();
    // refresh 가 한 번 실패하면 후속 401 은 즉시 throw — 알림·refresh 무한 루프 차단.
    private refreshFailed: boolean = false;

    constructor(
      private httpClient: HttpClient,
      private authService: AuthService,
      private utils: UtilsService,
      private dialog: MatDialog
    ) { }

    static getBaseUrl() {
      return HttpClientService.DOMAIN + HttpClientService.ROOT_URL;
    }

    static getDomain() {
      return HttpClientService.DOMAIN;
    }

    post(funcUrl: any, data: any, showLoading = true, httpOptions = this.httpOptions, requestTimeOut = HttpClientService.DEFAULT_TIMEOUT) {
      const url = HttpClientService.DOMAIN + HttpClientService.ROOT_URL + funcUrl;

      if (showLoading) {
        this.presentLoading(requestTimeOut);
      }
      return this.postWithoutLoading(url, data, httpOptions, requestTimeOut).pipe(finalize(() => {
        if (showLoading) {
          this.dissmissLoading();
        }
      }));
    }

    postWithoutLoading(url: any, data: any, httpOptions = this.httpOptions, requestTimeOut = HttpClientService.DEFAULT_TIMEOUT): Observable<Response> {
      return this.httpClient.post<Response>(url, data, httpOptions).pipe(
        retry(1),
        timeout(requestTimeOut),
        catchError((error: any) => {
          if(error instanceof TimeoutError) {
            const observable = new Observable<any>(observer => {
              setTimeout(() => {
                const response = {
                  resultCode: FAIL,
                  resultMsg: '응답 시간이 초과하였습니다.\n 다시 시도해주세요.'
                };
                observer.next(response);
                observer.complete();
              }, 10);
            });
            return observable;
          } else if(error.error.status === 404) {
            const observable = new Observable<any>(observer => {
              setTimeout(() => {
                const response = {
                  resultCode: FAIL,
                  resultMsg: '잘못된 요청입니다.\n 관리자에게 문의 바랍니다.'
                };
                observer.next(response);
                observer.complete();
              }, 100);
            });
            return observable;
          } else {
            return throwError(error);
          }
        })
    );
    }

    get(funcUrl: any, params: any = {}) {
        const url = HttpClientService.getBaseUrl() + funcUrl;
        return this.httpClient.get<Response>(url, params);
    }

    async saveAuth(auth: any) {
      this.authService.saveAuth(auth);
      this.refreshFailed = false;   // 새 토큰 저장 → refresh 실패 플래그 해제
    }

    async requestAndSaveAuth(id: string, password: string) {
      const auth = await this.requestAuth(id, password);
      this.authService.saveAuth(auth);
    }

    async requestAuth(id: string, password: string) {
      const url = HttpClientService.DOMAIN + '/oauth/token';
      const headerOption = {
        headers: this.authService.getAuthHttpHeader()
      };
      const body = this.authService.getAuthParams(id, password);
      const result = await this.postWithoutLoading(url, body, headerOption).toPromise() as any;
      return result;
    }

    requestRefreshAuth() {
      const url = HttpClientService.DOMAIN + '/oauth/token';
      const headerOption = {
        headers: this.authService.getAuthHttpHeader()
      };
      const body = this.authService.getRefreshAuthParams();
      return this.postWithoutLoading(url, body, headerOption);
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      const request: HttpRequest<any> = this.isRequestWithoutAuth(req.url)
        ? req.clone()
        : req.clone({ setHeaders: { Authorization: this.authService.getAuthToken() } });

      return next.handle(request).pipe(catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(error);
        }
        // refresh 호출 자체가 401 → 진짜 expired. 대기자들 깨우고 영구 실패 플래그.
        if (request.url.endsWith('token')) {
          this.isRefreshing = false;
          this.refreshFailed = true;
          this.refreshTokenSubject.next(null);
          this.handleError(error);
          return this.createFailResponse('refresh token expired');
        }
        // 이미 refresh 실패한 세션 — 후속 401 은 알림 없이 throw (무한 루프 차단)
        if (this.refreshFailed) {
          return throwError(error);
        }
        return this.handle401WithSingletonRefresh(req, next);
      }));
    }

    /**
     * 401 핸들링 — refresh 진행 중이면 대기 후 새 토큰으로 재시도. 진행 중이 아니면 refresh 시작.
     * 동일 refresh_token 으로 동시 호출되는 race 방지. (서버 reuseRefreshTokens(false) 대응)
     */
    private handle401WithSingletonRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      if (this.isRefreshing) {
        // 다른 호출이 refresh 중 — 큐 대기 후 새 토큰으로 재시도 (실패 시 throw)
        return this.refreshTokenSubject.pipe(
          take(1),
          switchMap(token => {
            if (token === null) return throwError(new Error('refresh failed'));
            return next.handle(this.addAuthHeader(req));
          })
        );
      }
      // 첫 401 — refresh 시작
      this.isRefreshing = true;

      return this.requestRefreshAuth().pipe(
        switchMap((data: any) => {
          this.isRefreshing = false;
          // 진짜 토큰 응답인지 검증 — fail response (createFailResponse 등) 가 들어와도 거른다.
          if (isNullOrUndefined(data) || !data.access_token) {
            this.refreshFailed = true;
            this.refreshTokenSubject.next(null);
            return throwError(new Error('refresh failed — no access_token in response'));
          }
          this.authService.saveAuth(data);
          this.refreshFailed = false;
          const newToken = this.authService.getAuthToken();
          this.refreshTokenSubject.next(newToken);   // 대기자들 깨움
          return next.handle(this.addAuthHeader(req));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshFailed = true;
          this.refreshTokenSubject.next(null);
          return throwError(err);
        })
      );
    }

    private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
      return req.clone({
        setHeaders: { Authorization: this.authService.getAuthToken() }
      });
    }

    private createFailResponse(resultMsg: string): Observable<any> {
      return new Observable<any>(observer => {
        setTimeout(() => {
          observer.next({ resultCode: FAIL, resultMsg });
          observer.complete();
        }, 100);
      });
    }

    handleError(error: any): void {
      switch (error.status) {
        case 400: {
          break;
        }
        case 401: {
          this.utils.openMessageDialog('로그인 필요', '오랜 기간동안 접속하지 않아 로그인이 필요합니다.', true, () => {
            this.authService.signOut();
          });
          break;
        }
        case 404: {
          this.utils.openMessageDialog('요청오류', '잘못된 요청입니다.\n 관리자에게 문의 바랍니다.')
          break;
        }
        case 405: {
          break;
        }
        default: {
        }
      }
    }

    isRequestWithoutAuth(url: string) {
      const funcUrl = url.replace(HttpClientService.getBaseUrl(), '');
      return EXCLUDE_AUTH_URL.includes(funcUrl) || url.endsWith('oauth/token');
    }

    removeAuth() {
      this.authService.removeAuth();
    }

    /**
     * 스피너 요청. LOADING_DELAY 안에 응답이 오면 아무것도 열지 않는다.
     * 동시 요청은 참조 카운트로 묶어 하나의 스피너만 쓴다 (forkJoin 등).
     */
    async presentLoading(timeOut = HttpClientService.DEFAULT_TIMEOUT) {
      this.loadingCount++;

      // 이미 떠 있거나 열리기를 기다리는 중이면 카운트만 올린다.
      if (this.loadingCount > 1 || this.dialogRef || this.loadingTimer) {
        return;
      }

      this.loadingTimer = setTimeout(() => {
        this.loadingTimer = null;
        if (this.loadingCount <= 0) {
          return;   // 기다리는 사이에 전부 끝났다.
        }

        const ref = this.dialog.open(ProgressSpinnerComponent, {
          data: {
            title: 'title',
            message: 'message'
          },
          autoFocus: false,
          disableClose: true,
          // 이 클래스가 없으면 Material 기본(라이트) 컨테이너가 그대로 나와
          // 다크 테마에서 스피너 뒤에 흰 상자가 생긴다. 스피너는 상자가 필요 없다.
          panelClass: 'progress-spinner-dialog',
        });
        this.dialogRef = ref;

        // 응답이 영영 안 오는 경우의 안전장치. 자신이 띄운 dialog 만 닫는다.
        setTimeout(() => {
          if (this.dialogRef === ref) {
            this.closeLoading();
          }
        }, timeOut);
      }, HttpClientService.LOADING_DELAY);
    }

    dissmissLoading() {
      this.loadingCount = Math.max(0, this.loadingCount - 1);
      if (this.loadingCount > 0) {
        return;   // 아직 남은 요청이 있다.
      }
      this.closeLoading();
    }

    /** 카운트와 무관하게 스피너를 정리한다. */
    private closeLoading() {
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
      }
      this.loadingCount = 0;
      this.dialogRef?.close();
      this.dialogRef = undefined;
    }
}